import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Facebook, Instagram, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import PageSetupGuide, { ApproveStep } from '@/components/PageSetupGuide';

const FB_MESSAGES = {
  cancelled: 'Facebook login was cancelled. Try again when you’re ready.',
  expired: 'That login link expired. Please try again.',
  failed: "We couldn't connect to Facebook. Please try again.",
};

/** onBackToAd: back to the ad they left to connect a Page (else "Create an ad" once it's ready);
 * profileVersion changes when the business details are saved, so the Page guide rewrites from them. */
export default function FacebookConnect({ onBackToAd = null, profileVersion = 0 }) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState('');
  const [setup, setSetup] = useState(false); // the "make a Page" guide is open

  const load = useCallback(() => api.get('/api/facebook/status').then(({ data }) => setStatus(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fb = params.get('fb');
    if (!fb) return;
    if (FB_MESSAGES[fb]) toast.error(FB_MESSAGES[fb]);
    if (fb === 'pick') api.get('/api/facebook/pages').then(({ data }) => setPages(data)).catch(err => toast.error(formatError(err.response?.data?.detail)));
    params.delete('fb');
    setParams(params, { replace: true });
  }, [params, setParams]);

  // Opened from elsewhere (e.g. the WhatsApp goal on the create ad page).
  useEffect(() => {
    if (params.get('setup') !== 'page') return;
    setSetup(true);
    params.delete('setup');
    setParams(params, { replace: true });
  }, [params, setParams]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setBusy('');
  };
  const login = () => run('login', async () => { window.location.href = (await api.get('/api/facebook/login-url')).data.url; });
  const pick = (id) => run(id, async () => {
    setStatus((await api.post('/api/facebook/select-page', { page_id: id })).data);
    setPages(null);
    refreshUser();
  });
  // Back from making a Page with the guide: the one new Page is picked for them.
  useEffect(() => {
    const fresh = (pages || []).find(p => p.suggested);
    if (fresh) pick(fresh.id);
  }, [pages]); // eslint-disable-line react-hooks/exhaustive-deps
  const check = () => run('check', async () => { setStatus((await api.post('/api/facebook/check-access')).data); refreshUser(); });

  if (!status) return null;
  const page = status.page;
  // The guide shows when they asked for it, when Facebook found no Pages, or while they're partway through.
  const guiding = status.configured && !(page && page.share_status === 'approved')
    && (setup || pages?.length === 0 || (!pages && ['details', 'whatsapp', 'portfolio', 'connect', 'approve'].includes(status.page_setup)));

  return (
    <div className="space-y-4 p-6 rounded-2xl bg-card border-2 border-ink shadow-neo mt-6" data-testid="facebook-connect">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1877F2] flex items-center justify-center"><Facebook className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="text-lg font-extrabold font-heading">Facebook Page</h2>
          <p className="text-xs text-muted-foreground">Your ads show your Page's name and photo. We never post without you.</p>
        </div>
      </div>

      {!status.configured ? (
        <p className="text-sm text-muted-foreground">Facebook connection opens soon.</p>
      ) : guiding ? (
        <PageSetupGuide onLogin={login} page={page} onCheck={check} checking={busy === 'check'} profileVersion={profileVersion} />
      ) : pages ? (
        <div className="space-y-2">
          <p className="text-sm font-semibold">Which Page should your ads come from?</p>
          {status.page_setup === 'connect' && !pages.some(p => p.suggested) && (
            <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="new-page-missing">
              Don’t see your new Page? Log in again, and when Facebook says “Continue as…”, tap <b>Edit access</b> and tick your new Page.{' '}
              <button onClick={login} className="underline font-semibold">Log in again</button>
            </p>
          )}
          {pages.map(p => (
            <button key={p.id} onClick={() => pick(p.id)} disabled={!!busy}
              className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-ink/25 hover:border-ink hover:bg-butter/50 text-left">
              {p.picture ? <img src={p.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full gradient-bg" />}
              <div className="flex-1">
                <p className="text-sm font-semibold">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.instagram ? `Instagram: @${p.instagram}` : 'No Instagram linked'}</p>
              </div>
              {busy === p.id && <Loader2 className="w-4 h-4 animate-spin" />}
            </button>
          ))}
        </div>
      ) : page ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-butter/50 border-2 border-ink/15">
            {page.picture ? <img src={page.picture} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full gradient-bg" />}
            <div className="flex-1">
              <p className="text-sm font-semibold">{page.page_name}</p>
              {page.instagram_username && <p className="text-xs text-muted-foreground flex items-center gap-1"><Instagram className="w-3 h-3" />@{page.instagram_username}</p>}
            </div>
            {page.share_status === 'approved'
              ? <span className="text-xs font-bold text-money flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Ready</span>
              : <span className="text-xs font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1"><Clock className="w-4 h-4" /> Waiting</span>}
          </div>
          {page.share_status !== 'approved' && (
            <div className="space-y-2">
              <p className="text-sm font-semibold">One last step: approve our request</p>
              {page.share_error && <p className="text-xs text-amber-700 dark:text-amber-400">{page.share_error}</p>}
              <ApproveStep page={null} onCheck={check} checking={busy === 'check'} />
            </div>
          )}
          {page.share_status === 'approved' && (onBackToAd
            ? <Button onClick={onBackToAd} className="w-full gradient-bg" data-testid="page-ready-back-to-ad">Your Page is ready. Back to your ad</Button>
            : <Button onClick={() => navigate('/create-ad')} className="w-full gradient-bg" data-testid="page-ready-create-ad">Your Page is ready. Create an ad</Button>)}
          <button onClick={login} className="text-xs text-muted-foreground underline">Use a different Page</button>
        </div>
      ) : (
        <div className="space-y-2">
          <Button onClick={login} disabled={busy === 'login'} className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white" data-testid="connect-facebook-btn">
            {busy === 'login' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Facebook className="w-4 h-4 mr-1" />} Continue with Facebook
          </Button>
          <Button variant="outline" onClick={() => setSetup(true)} className="w-full" data-testid="make-page-link">
            No Facebook Page yet? We’ll help you make one
          </Button>
        </div>
      )}
      <PastAdsNote past={status.past_ads} />
    </div>
  );
}

const RESULT_WORDS = { messages: 'messages', leads: 'leads', calls: 'calls', purchases: 'sales', 'website visits': 'website visits' };

// What we learned from the ads they ran themselves before Rayogent (read when they log in with Facebook).
function PastAdsNote({ past }) {
  if (!past?.status) return null;
  if (past.status === 'done' && past.best) {
    return (
      <p className="text-xs text-muted-foreground p-3 rounded-xl bg-money-soft/40 border-2 border-ink/10" data-testid="past-ads-note">
        We looked at {past.ads} of your past ads. Your best got {past.best.results} {RESULT_WORDS[past.best.result_type] || past.best.result_type} at
        about ₹{Math.round(past.best.cpr_paise / 100)} each. Your new ads learn from what worked.
      </p>
    );
  }
  if (past.status === 'no_permission') {
    return (
      <p className="text-xs text-muted-foreground" data-testid="past-ads-note">
        Tip: when you log in with Facebook, allow Rayogent to see your ads. We'll learn from the ads you ran yourself.
      </p>
    );
  }
  return null;
}
